import { useEffect, useState } from "react";
import { Sidebar } from "@/components/Sidebar";
import { Header } from "@/components/Header";
import { AuthGate } from "@/components/AuthGate";
import { useAuth } from "@/hooks/useAuth";
import { CommandPalette } from "@/components/CommandPalette";
import { ChatProvider, useChat } from "@/components/ChatContext";
import { FelixDrawer } from "@/components/FelixChat";
import { TopCountdownBanner } from "@/components/TopCountdownBanner";
import type { NavId } from "@/data/nav";
import { OverviewPanel } from "@/panels/OverviewPanel";
import { TimelinePanel } from "@/panels/TimelinePanel";
import { DeliverablesPanel } from "@/panels/DeliverablesPanel";
import { DeliverablesCardPanel } from "@/panels/DeliverablesCardPanel";
import { WinsAppShell } from "@/wins-app/WinsApp";
import { TTVPanel } from "@/panels/TTVPanel";
import { RisksPanel } from "@/panels/RisksPanel";
import { PulsePanel } from "@/panels/PulsePanel";
import { CriteriaPanel } from "@/panels/CriteriaPanel";
import { ProposalsPanel } from "@/panels/ProposalsPanel";
import { CalendarPanel } from "@/panels/CalendarPanel";
import { AdminSettingsPanel } from "@/panels/AdminSettingsPanel";
import { OrinAdminPanel } from "@/panels/OrinAdminPanel";
import { canSeeProposals } from "@/lib/access";
import { cn } from "@/lib/utils";

const THEME_KEY = "felix:theme";
const ACTIVE_KEY = "felix:active";

function readTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  const saved = window.localStorage.getItem(THEME_KEY);
  if (saved === "light" || saved === "dark") return saved;
  return "light";
}

function readActive(): NavId {
  if (typeof window === "undefined") return "overview";
  const saved = window.localStorage.getItem(ACTIVE_KEY) as NavId | null;
  return saved ?? "overview";
}

const SHORTCUTS: Record<string, NavId> = {
  o: "overview",
  t: "timeline",
  d: "tasks",
  k: "calendar",
  s: "admin",
  i: "orin_admin",
  b: "deliverables",
  w: "wins",
  m: "ttv",
  r: "risks",
  p: "pulse",
  c: "criteria",
  x: "proposals",
};

function AppShell() {
  const { user, isLoading } = useAuth();
  const { drawerOpen } = useChat();
  const [active, setActive] = useState<NavId>(readActive);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">(readTheme);
  const [awaitingG, setAwaitingG] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(ACTIVE_KEY, active);
  }, [active]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const inField =
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (inField) return;
      if (e.key === "g") {
        setAwaitingG(true);
        setTimeout(() => setAwaitingG(false), 1200);
        return;
      }
      if (awaitingG) {
        const target = SHORTCUTS[e.key.toLowerCase()];
        if (target) {
          e.preventDefault();
          setActive(target);
        }
        setAwaitingG(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [awaitingG]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <AuthGate />;

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <TopCountdownBanner />
      <div className="flex min-h-0 flex-1">
      <Sidebar
        active={active}
        onSelect={setActive}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <div
        className={cn(
          "flex flex-1 flex-col overflow-hidden transition-[padding] duration-200",
          drawerOpen && "sm:pr-[420px] lg:pr-[460px] xl:pr-[500px]"
        )}
      >
        <Header
          active={active}
          onOpenCommand={() => setPaletteOpen(true)}
          theme={theme}
          onToggleTheme={() => setTheme((t) => (t === "light" ? "dark" : "light"))}
          onOpenMobileSidebar={() => setMobileSidebarOpen(true)}
        />

        <main className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 md:px-7 md:py-6">
          <div className="mx-auto max-w-6xl animate-slide-up">
            {active === "overview" && <OverviewPanel onJumpTo={setActive} />}
            {active === "timeline" && <TimelinePanel />}
            {active === "tasks" && <DeliverablesPanel />}
            {active === "calendar" && <CalendarPanel />}
            {active === "admin" && <AdminSettingsPanel />}
            {active === "orin_admin" && <OrinAdminPanel />}
            {active === "deliverables" && <DeliverablesCardPanel />}
            {active === "wins" && <WinsAppShell embedded userName={user?.name ?? ""} />}
            {active === "ttv" && <TTVPanel />}
            {active === "risks" && <RisksPanel />}
            {active === "pulse" && <PulsePanel />}
            {active === "criteria" && <CriteriaPanel />}
            {active === "proposals" && (canSeeProposals(user?.email) ? <ProposalsPanel /> : <OverviewPanel onJumpTo={setActive} />)}
          </div>
        </main>
      </div>
      </div>

      <FelixDrawer />

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={setActive}
      />

      {awaitingG && (
        <div className="pointer-events-none fixed bottom-4 left-1/2 z-40 -translate-x-1/2 rounded-md border border-border bg-card px-3 py-1.5 text-caption text-muted-foreground shadow-card">
          g + <kbd className="rounded bg-muted px-1">o/t/d/b/w/m/r/p/c</kbd> to jump
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <ChatProvider>
      <AppShell />
    </ChatProvider>
  );
}
